import type { RumGlobal } from '@datadog/browser-rum';
import type * as DatadogActions from '@/types/datadogActions';

declare global {
    interface Window {
        DD_RUM?: RumGlobal;
    }
}

const datadogSkipped = () => import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEV_DD !== 'true';

type MaybeArg<T> = [T] extends [undefined] ? [] : [T & object];
type Handler<K extends keyof ActionContexts> =
    (...args: MaybeArg<ActionContexts[K]>) => void;
type Registry = { [K in keyof ActionContexts]: Handler<K> };

function action<K extends keyof ActionContexts>(name: K) {
    return ((...args: MaybeArg<ActionContexts[K]>) => {
        if (!window.DD_RUM) return;

        if (import.meta.env.DEV) {
            console.debug('[Datadog] Action:', { name, ctx: args[0] });
        }

        if (datadogSkipped()) return;

        return window.DD_RUM.addAction(name, args[0] as object | undefined);
    }) as Handler<K>;
}

function addDatadogAction<T extends keyof typeof ActionRegistry>(
    type: T,
    ...args: Parameters<typeof ActionRegistry[T]>
) {
    (ActionRegistry[type] as (...args: Parameters<typeof ActionRegistry[T]>) => void)(...args);
}

interface ActionContexts {
    'WebViewer:SignatureAdded': DatadogActions.SignatureAddedCtx;
    'WebViewer:FieldChanged': DatadogActions.FieldChangedCtx;
    'WebViewer:CompletedSigning': DatadogActions.CompletedSigningCtx;
    'WebViewer:TemplateDocumentRejected': DatadogActions.TemplateDocumentRejectedCtx;
    'WebViewer:SignatureCheckFailed': DatadogActions.SignatureCheckFailedCtx;
    'Reviewer:Approved': DatadogActions.ReviewerActionCtx;
    'Reviewer:RequestedChanges': DatadogActions.ReviewerActionCtx;
    'AdminAcceptedRedlines': DatadogActions.AdminActionCtx;
}

const ActionRegistry: Registry = {
    'WebViewer:SignatureAdded': action('WebViewer:SignatureAdded'),
    'WebViewer:FieldChanged': action('WebViewer:FieldChanged'),
    'WebViewer:CompletedSigning': action('WebViewer:CompletedSigning'),
    'WebViewer:TemplateDocumentRejected': action('WebViewer:TemplateDocumentRejected'),
    'WebViewer:SignatureCheckFailed': action('WebViewer:SignatureCheckFailed'),
    'Reviewer:Approved': action('Reviewer:Approved'),
    'Reviewer:RequestedChanges': action('Reviewer:RequestedChanges'),
    'AdminAcceptedRedlines': action('AdminAcceptedRedlines'),
};

export {
    datadogSkipped,
    addDatadogAction,
};
